import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { extractIdFromSlugPath, buildBlogSlugPath } from "../../utils/slugify";

// BASE_URL points to the "api" FOLDER (no filename), because we build
// two different endpoints from it below: blog.php (single post)
// and blogs.php (full list, for the sidebar).
const BASE_URL = "/admin/api";

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function BlogDetail() {
  // The URL param looks like "ready-to-find-the-best-ibdp-...-8"
  const { slugId } = useParams();
  const id = extractIdFromSlugPath(slugId);

  // Main article state
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sidebar (all posts) state
  const [allPosts, setAllPosts] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(true);

  // Fetch the single article whenever the id changes
  useEffect(() => {
    if (!id) {
      setError("Invalid blog URL");
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    axios
      .get(`${BASE_URL}/blog.php`, { params: { id } })
      .then((res) => {
        if (!isMounted) return;
        if (res.data.success === false) {
          throw new Error(res.data.message || "Blog not found");
        }
        const blogData = res.data.blog ?? res.data;
        setPost(blogData);
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        const message =
          err.response?.data?.message || err.message || "Something went wrong";
        setError(message);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  // Fetch the full list once, for the sidebar ("All Blogs")
  useEffect(() => {
    let isMounted = true;

    axios
      .get(`${BASE_URL}/blogs.php`)
      .then((res) => {
        if (!isMounted) return;
        const data = res.data;
        const list = data.blogs ?? (Array.isArray(data) ? data : []);
        setAllPosts(list);
        setSidebarLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setSidebarLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {post?.blog_seo_title || post?.blog_title || "Blog - Websiteseo"}
        </title>
        {(post?.blog_seo_description || post?.blog_content) && (
          <meta
            name="description"
            content={
              post?.blog_seo_description ||
              (post?.blog_content || "").replace(/<[^>]*>/g, "").slice(0, 160)
            }
          />
        )}
        <link rel="canonical" href={`https://www.websiteseo.ae/blog/${slugId}`} />
      </Helmet>

      {/* Scoped styles — matches the professional card style used on /blog */}
      <style>{`
        .blog-detail-hero-img {
          width: 100%;
          height: 380px;
          object-fit: cover;
          border-radius: 14px;
          margin-bottom: 28px;
        }
        .blog-detail-date {
          font-size: 13px;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          color: #8a8a8a;
          font-weight: 600;
          margin-bottom: 10px;
          display: block;
        }
        p {
          text-align: justify;
        }
          .srl ul li {
            list-style: disc;
            margin-left: 25px;
            margin-bottom: 10px;
          }
        .blog-detail-content {
          font-size: 16.5px;
          line-height: 1.85;
          color: #333;
        }
        .blog-detail-content p {
          margin-bottom: 18px;
        }
        .blog-detail-content img {
          max-width: 100%;
          border-radius: 10px;
          margin: 20px 0;
        }
        .blog-detail-content h2,
        .blog-detail-content h3 {
          margin-top: 32px;
          margin-bottom: 16px;
          font-weight: 700;
        }

        .blog-sidebar-card {
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 4px 18px rgba(0,0,0,0.06);
          padding: 26px 24px;
          position: sticky;
          top: 100px;
        }
        .blog-sidebar-title {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 22px;
          padding-bottom: 14px;
          border-bottom: 2px solid #f0f0f0;
        }
        .blog-sidebar-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 12px 8px;
          border-radius: 10px;
          text-decoration: none;
          color: #1c1c1c;
          transition: background 0.2s ease;
          margin-bottom: 4px;
        }
        .blog-sidebar-item:hover {
          background: #f6f6f6;
          text-decoration: none;
        }
        .blog-sidebar-item.is-active {
          background: #f2e9f7;
        }
        .blog-sidebar-item-img {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .blog-sidebar-item-title {
          font-size: 14.5px;
          line-height: 1.4;
          font-weight: 500;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .blog-sidebar-item.is-active .blog-sidebar-item-title {
          color: #7a1f9c;
          font-weight: 700;
        }
      `}</style>

      <div>
        {/* HERO / BREADCRUMB BANNER */}
        <div className="inner-page-banner-wrap">
          <div className="container">
            <h3>{post?.blog_title || "Blog"}</h3>
            <ul>
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/blog">Blog</Link>
              </li>
              <li>{post?.blog_title || "Article"}</li>
            </ul>
          </div>
        </div>

        <section className="ct-01 content-section division py-80">
          <div className="container">
            <div className="row srl">
              {/* MAIN ARTICLE COLUMN */}
              <div className="col-lg-8">
                {loading && <p className="fs-18">Loading article…</p>}

                {!loading && error && (
                  <div>
                    <p className="fs-18">
                      Couldn&apos;t load this article right now. ({error})
                    </p>
                    <Link
                      to="/blog"
                      className="btn r-08 btn--tra-black hover--plum mt-3"
                    >
                      Back to Blog
                    </Link>
                  </div>
                )}

                {!loading && !error && post && (
                  <>
                    {post.blog_image && (
                      <img
                        src={post.blog_image}
                        alt={post.blog_title}
                        className="blog-detail-hero-img"
                      />
                    )}

                    {post.blog_date && (
                      <span className="blog-detail-date">
                        {formatDate(post.blog_date)}
                      </span>
                    )}

                    <h1 className="fs-46 mb-4">{post.blog_title}</h1>

                    {/* blog_content is HTML from the CMS/editor, rendered as-is */}
                    <div
                      className="blog-detail-content"
                      dangerouslySetInnerHTML={{ __html: post.blog_content }}
                    />

                    <div className="mt-5">
                      <Link
                        to="/blog"
                        className="btn r-08 btn--tra-black hover--plum"
                      >
                        ← Back to Blog
                      </Link>
                    </div>
                  </>
                )}
              </div>
              {/* END MAIN ARTICLE COLUMN */}

              {/* SIDEBAR — ALL BLOGS */}
              <div className="col-lg-4 mt-5 mt-lg-0">
                <div className="blog-sidebar-card">
                  <h5 className="blog-sidebar-title">All Blogs</h5>

                  {sidebarLoading && <p className="fs-15">Loading…</p>}

                  {!sidebarLoading && allPosts.length === 0 && (
                    <p className="fs-15">No other posts yet.</p>
                  )}

                  {!sidebarLoading && allPosts.length > 0 && (
                    <div>
                      {allPosts.map((item) => {
                        const isActive = String(item.blog_id) === String(id);
                        return (
                          <Link
                            key={item.blog_id}
                            to={`/blog/${buildBlogSlugPath(
                              item.blog_title,
                              item.blog_id
                            )}`}
                            className={`blog-sidebar-item${
                              isActive ? " is-active" : ""
                            }`}
                          >
                            {item.blog_image && (
                              <img
                                src={item.blog_image}
                                alt={item.blog_title}
                                className="blog-sidebar-item-img"
                              />
                            )}
                            <span className="blog-sidebar-item-title">
                              {item.blog_title}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {/* END SIDEBAR */}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default BlogDetail;
